import React, { useState, useEffect } from "react";
import axios from "axios";
import { FaPlus, FaEdit, FaTrash, FaCheck, FaTimes, FaSearch } from "react-icons/fa";
import { useAuth } from "../context/AuthContext";

const API_BASE_URL = import.meta.env.VITE_URL || import.meta.env.VITE_URL2 || "http://localhost:3000";

export default function AdminTourAndTravel() {
  const [activeTab, setActiveTab] = useState("country");

  // Data
  const [countries, setCountries] = useState([]);
  const [packages, setPackages] = useState([]);
  const [inquiries, setInquiries] = useState([]);

  // UI State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Form State
  const [countryForm, setCountryForm] = useState({ name: "" });
  const [packageForm, setPackageForm] = useState({
    tourCountryId: "",
    cityName: "",
    title: "",
    shortDescription: "",
    document: null,
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === "country") {
        const res = await axios.get(`${API_BASE_URL}/api/tour/admin/countries`, { withCredentials: true });
        setCountries(res.data.data || []);
      } else if (activeTab === "city") {
        const res = await axios.get(`${API_BASE_URL}/api/tour/admin/packages`, { withCredentials: true });
        setPackages(res.data.data || []);
        if (countries.length === 0) {
          const cRes = await axios.get(`${API_BASE_URL}/api/tour/admin/countries`, { withCredentials: true });
          setCountries(cRes.data.data || []);
        }
      } else if (activeTab === "booking") {
        const res = await axios.get(`${API_BASE_URL}/api/tour/admin/inquiries`, { withCredentials: true });
        setInquiries(res.data.data || []);
      }
    } catch (err) {
      setError(err.response?.data?.error || "Error fetching data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const openAddModal = () => {
    setIsEdit(false);
    setEditingId(null);
    setCountryForm({ name: "" });
    setPackageForm({ tourCountryId: "", cityName: "", title: "", shortDescription: "", document: null });
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setIsEdit(true);
    setEditingId(item._id);
    if (activeTab === "country") {
      setCountryForm({ name: item.name });
    } else if (activeTab === "city") {
      setPackageForm({
        tourCountryId: item.tourCountryId?._id || item.tourCountryId,
        cityName: item.cityName,
        title: item.title,
        shortDescription: item.shortDescription,
        document: null,
      });
    }
    setShowModal(true);
  };

  const handleCountrySubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEdit) {
        await axios.put(`${API_BASE_URL}/api/tour/admin/countries/${editingId}`, countryForm, { withCredentials: true });
      } else {
        await axios.post(`${API_BASE_URL}/api/tour/admin/countries`, countryForm, { withCredentials: true });
      }
      setShowModal(false);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || "Error saving country");
    }
  };

  const handlePackageSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append("tourCountryId", packageForm.tourCountryId);
      formData.append("cityName", packageForm.cityName);
      formData.append("title", packageForm.title);
      formData.append("shortDescription", packageForm.shortDescription);
      if (packageForm.document) {
        formData.append("document", packageForm.document);
      }

      const headers = { "Content-Type": "multipart/form-data" };
      if (isEdit) {
        await axios.put(`${API_BASE_URL}/api/tour/admin/packages/${editingId}`, formData, { withCredentials: true, headers });
      } else {
        await axios.post(`${API_BASE_URL}/api/tour/admin/packages`, formData, { withCredentials: true, headers });
      }
      setShowModal(false);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || "Error saving package");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure?")) return;
    try {
      const endpoint = activeTab === "country" ? "countries" : "packages";
      await axios.delete(`${API_BASE_URL}/api/tour/admin/${endpoint}/${id}`, { withCredentials: true });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || "Error deleting");
    }
  };

  const updateInquiryStatus = async (id, status) => {
    try {
      await axios.put(`${API_BASE_URL}/api/tour/admin/inquiries/${id}/status`, { status }, { withCredentials: true });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || "Error updating status");
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Tour and Package Management</h2>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6 space-x-8">
        {["country", "city", "booking"].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`py-2 px-4 border-b-2 font-medium text-sm transition-colors ${activeTab === tab
              ? "border-blue-500 text-blue-600"
              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">{error}</div>}

      <div className="bg-white rounded-xl shadow-sm border p-4">
        {/* Country Tab */}
        {activeTab === "country" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Countries</h3>
              <button onClick={openAddModal} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center">
                <FaPlus className="mr-2" /> Add Country
              </button>
            </div>
            {loading ? <p>Loading...</p> : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr><th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th><th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th></tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {countries.map(c => (
                    <tr key={c._id}>
                      <td className="px-6 py-4 whitespace-nowrap">{c.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button onClick={() => openEditModal(c)} className="text-blue-600 hover:text-blue-900 mr-4"><FaEdit /></button>
                        <button onClick={() => handleDelete(c._id)} className="text-red-600 hover:text-red-900"><FaTrash /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* City (Package) Tab */}
        {activeTab === "city" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Cities / Packages</h3>
              <button onClick={openAddModal} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center">
                <FaPlus className="mr-2" /> Add City Package
              </button>
            </div>
            {loading ? <p>Loading...</p> : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Country</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">City Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {packages.map(p => (
                    <tr key={p._id}>
                      <td className="px-6 py-4 whitespace-nowrap">{p.tourCountryId?.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{p.cityName}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{p.title}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button onClick={() => openEditModal(p)} className="text-blue-600 hover:text-blue-900 mr-4"><FaEdit /></button>
                        <button onClick={() => handleDelete(p._id)} className="text-red-600 hover:text-red-900"><FaTrash /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Booking Tab */}
        {activeTab === "booking" && (
          <div>
            <h3 className="text-lg font-semibold mb-4">Inquiries / Bookings</h3>
            {loading ? <p>Loading...</p> : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Package (City)</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {inquiries.map(inq => (
                    <tr key={inq._id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900">{inq.userId?.name}</div>
                        <div className="text-xs text-gray-500">{inq.userId?.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900">{inq.tourPackageId?.title}</div>
                        <div className="text-xs text-gray-500">{inq.tourPackageId?.cityName} ({inq.tourPackageId?.tourCountryId?.name})</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(inq.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${inq.status === 'Confirm' ? 'bg-green-100 text-green-800' :
                          inq.status === 'Reject' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                          {inq.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {inq.status === 'Pending' && (
                          <div className="flex justify-end gap-2">
                            <button onClick={() => updateInquiryStatus(inq._id, 'Confirm')} className="text-green-600 hover:text-green-900"><FaCheck title="Confirm" /></button>
                            <button onClick={() => updateInquiryStatus(inq._id, 'Reject')} className="text-red-600 hover:text-red-900"><FaTimes title="Reject" /></button>
                          </div>
                        )}
                        {inq.status !== 'Pending' && (
                          <span className="text-gray-400">Processed</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {showModal && activeTab === "country" && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">{isEdit ? "Edit Country" : "Add Country"}</h3>
            <form onSubmit={handleCountrySubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Country Name</label>
                <input
                  type="text"
                  value={countryForm.name}
                  onChange={e => setCountryForm({ ...countryForm, name: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                />
              </div>
              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border rounded-lg">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg">{isEdit ? "Update" : "Save"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showModal && activeTab === "city" && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-xl">
            <h3 className="text-xl font-bold mb-4">{isEdit ? "Edit City Package" : "Add City Package"}</h3>
            <form onSubmit={handlePackageSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                <select
                  value={packageForm.tourCountryId}
                  onChange={e => setPackageForm({ ...packageForm, tourCountryId: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                >
                  <option value="">Select Country</option>
                  {countries.map(c => (
                    <option key={c._id} value={c._id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City Name</label>
                <input
                  type="text"
                  value={packageForm.cityName}
                  onChange={e => setPackageForm({ ...packageForm, cityName: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={packageForm.title}
                  onChange={e => setPackageForm({ ...packageForm, title: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Short Description</label>
                <textarea
                  value={packageForm.shortDescription}
                  onChange={e => setPackageForm({ ...packageForm, shortDescription: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  rows="3"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Document (Optional)</label>
                <input
                  type="file"
                  onChange={e => setPackageForm({ ...packageForm, document: e.target.files[0] })}
                  className="w-full px-4 py-2 border rounded-lg"
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                />
                {isEdit && <p className="text-xs text-gray-500 mt-1">Leave blank to keep existing document</p>}
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border rounded-lg">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg">{isEdit ? "Update" : "Save"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
